import cn from 'classnames';
import events from 'events-bus';
import { useState, useEffect, useRef, useMemo } from 'react';

import { useCheckoutContext, Shipment } from '@zooplus/checkout-state';

import CartAutoshipment from '../cart-autoshipment';
import CartTable from '../cart-table';

import FeedbackMessage from '@components/common/notifications/feedback-message';
import OrderSummary from '@components/common/order-summary';
import PageCommonLayout from '@components/common/page-common-layout';
import RecommendationsCarousel from '@components/common/recommendations-carousel';
import { useIsResponsiveViewport } from '@hooks/use-is-responsive-viewport';
import usePixelExecutor from '@hooks/use-pixel-executor';
import { useTranslation } from '@hooks/use-translations';
import commonStyles from '@styles/common.module.scss';
import {
  PAGE_TYPE,
  VIEWPORT,
  MESSAGE_PLACEMENT,
  CHECKOUT_STATE_PROJECT_ID,
  MINI_AUTOSHIPMENT_BOX,
  CART_VERSION,
  MESSAGE_CODE,
} from '@utilities/constants';
import { hasMessageWithCode } from '@utilities/utilities';
import {
  isObjectNotNullOrEmpty,
  applyStyleBasedOnResolution,
  isMobileViewport,
} from '@utilities/utils';

const CartOverview = () => {
  const { prevArticle, setPageType, checkoutState, cartVersion, pageType } =
    useCheckoutContext(CHECKOUT_STATE_PROJECT_ID);
  usePixelExecutor();
  const { t } = useTranslation();

  const [mobileOrDesktopViewport, setMobileOrDesktopViewport] = useState(() => {
    return isMobileViewport() ? VIEWPORT.MOBILE : VIEWPORT.DESKTOP;
  });
  const isResponsiveViewport = useIsResponsiveViewport();

  const isMinOrderError = useMemo(
    () =>
      hasMessageWithCode(checkoutState?.cart?.cartMessages, MESSAGE_CODE.CHECKOUT_ERROR_MINORDER),
    [checkoutState?.cart?.cartMessages],
  );

  const keyCloackToken = useRef<string | undefined>();
  const stickyCheckoutButtonRef = useRef<HTMLDivElement | null>(null);

  const articles = checkoutState?.cart?.articles;
  const shipments = checkoutState?.cart?.shipments;
  const shipmentWithArticles = shipments?.filter(
    (shipment: Shipment) => shipment?.articles?.length,
  );

  const isCartApiV2: boolean = cartVersion === CART_VERSION.V2;
  let has3pArticles: boolean = isCartApiV2 && !!shipmentWithArticles?.length;
  // if only 1P articles
  if (
    shipmentWithArticles?.length === 1 &&
    [shipmentWithArticles[0]?.orderType, shipmentWithArticles[0]?.seller.sellerType].includes('1P')
  ) {
    has3pArticles = false;
  }

  const isFirstOrderDiscountAvailable = articles?.some(
    (article) => article.subscriptionDetails?.firstOrderDiscount,
  );

  const cartProducts = useMemo(() => {
    return articles?.map((article) => article.psId)?.join(',');
  }, [articles]);

  const cartArticles = useMemo(() => {
    return articles?.map((article) => article.id)?.join(',');
  }, [articles]);

  useEffect(() => {
    const handleResize = () => {
      setMobileOrDesktopViewport(isMobileViewport() ? VIEWPORT.MOBILE : VIEWPORT.DESKTOP);
    };
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    const isRestoreArticlePresent = isObjectNotNullOrEmpty(prevArticle);

    applyStyleBasedOnResolution('.z-snackbar-queue', 'bottom', 'max-width', '543px', {
      mobile: isRestoreArticlePresent ? '9.8125rem' : '0',
      desktop: isRestoreArticlePresent ? '8.8125rem' : '2.8125rem',
    });
  }, [prevArticle, mobileOrDesktopViewport]);

  useEffect(() => {
    setPageType?.(PAGE_TYPE.WEB_CART);
  }, [setPageType]);

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    events.on('AUTH-SUCCESS-EVENT-v1', (_eventName: any, data: any) => {
      keyCloackToken.current = data.token;
    });
  }, []);

  const leftColumnContent = (
    <>
      <div className={cn(!isFirstOrderDiscountAvailable && 'pp-mt-0')}>
        <CartTable keyCloackToken={keyCloackToken} has3pArticles={has3pArticles} />
      </div>

      {!has3pArticles && <CartAutoshipment keyCloackToken={keyCloackToken} />}
    </>
  );

  const rightColumnContent = <OrderSummary stickyCheckoutButtonRef={stickyCheckoutButtonRef} />;

  const recommendationContent = pageType === PAGE_TYPE.WEB_CART && cartProducts && (
    <RecommendationsCarousel
      pageType={PAGE_TYPE.WEB_CART}
      cartProducts={cartProducts}
      cartArticles={cartArticles}
    />
  );

  const mobileViewCheckoutButton = (
    <section
      className={commonStyles.checkoutButtonSection}
      aria-label={t('cart.summary.proceedToCheckout')}>
      <FeedbackMessage
        placement={MESSAGE_PLACEMENT.CART_OVERVIEW_BOTTOM_RIGHT}
        customStyle={cn('pp-mx-m', isMinOrderError && 'pp-mb-m')}
      />
      <div
        ref={stickyCheckoutButtonRef}
        className={cn(
          commonStyles.cartButtonBox,
          isMinOrderError && commonStyles.cartButtonBox__invisible,
        )}>
        <div id={MINI_AUTOSHIPMENT_BOX.IS_STICKY}></div>
      </div>
    </section>
  );

  return (
    <PageCommonLayout
      pageHeading={t('cart.overview.title')}
      leftColumnContent={leftColumnContent}
      leftColumnAriaLabel={t('cart.table.header.title')}
      rightColumnContent={rightColumnContent}
      recommendationContent={recommendationContent}
      mobileViewCheckoutButton={mobileViewCheckoutButton}
      feedbackMessage={
        !isResponsiveViewport && (
          <FeedbackMessage placement={MESSAGE_PLACEMENT.CART_OVERVIEW_BOTTOM_RIGHT} />
        )
      }
    />
  );
};

export default CartOverview;
